import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import {
  type CustomerAccountListItem,
  companyAccountsQueryOptions,
} from '@/entities/customer-account'
import { GrantProjectAccessDialog } from '@/features/grant-project-access'
import { AccountDialog, useSetAccountStatus } from '@/features/manage-customer-account'
import { pluralRu } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Chip } from '@/shared/ui/chip'
import { EmptyState } from '@/shared/ui/empty-state'
import { Icon } from '@/shared/ui/icon'
import { Menu } from '@/shared/ui/menu'
import { Spinner } from '@/shared/ui/spinner'
import { StatusBadge } from '@/shared/ui/status-badge'
import { StatusScreen } from '@/shared/ui/status-screen'
import { Table, TBody, TD, TH, THead, TR } from '@/shared/ui/table'
import { toast } from '@/shared/ui/toast'

interface CompanyPeopleProps {
  companyId: string
  canManage: boolean
}

type Dialog =
  | { kind: 'create' }
  | { kind: 'edit'; account: CustomerAccountListItem }
  | { kind: 'access'; account: CustomerAccountListItem }
  | null

// Люди компании: таблица аккаунтов + управление (создание/правка/статус/доступ).
export function CompanyPeople({ companyId, canManage }: CompanyPeopleProps) {
  const accountsQ = useQuery(companyAccountsQueryOptions(companyId))
  const setStatus = useSetAccountStatus(companyId)
  const [dialog, setDialog] = useState<Dialog>(null)

  function toggleStatus(account: CustomerAccountListItem) {
    const active = account.status !== 'active'
    setStatus.mutate(
      { id: account.id, active },
      {
        onSuccess: () => toast.success(active ? 'Доступ восстановлен' : 'Доступ отозван'),
        onError: () => toast.error('Не удалось обновить'),
      },
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {accountsQ.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : accountsQ.error ? (
        <StatusScreen title="Не удалось загрузить людей" message="Обновите страницу." />
      ) : accountsQ.data.length === 0 ? (
        <EmptyState
          title="Людей нет"
          description="Добавьте первого человека компании."
          {...(canManage
            ? {
                action: (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setDialog({ kind: 'create' })}
                  >
                    <Icon name="user-plus" size={16} />
                    Человек
                  </Button>
                ),
              }
            : {})}
        />
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Имя</TH>
              <TH>Логин</TH>
              <TH>Роль</TH>
              <TH>Статус</TH>
              <TH>Доступ</TH>
              {canManage && (
                // Добавление живёт в самом хедере таблицы — отдельной кнопкой над
                // ней оно висело в воздухе.
                <TH className="w-10 text-right">
                  <button
                    type="button"
                    aria-label="Добавить человека"
                    title="Добавить человека"
                    onClick={() => setDialog({ kind: 'create' })}
                    className="inline-flex rounded-control p-1 text-text-tertiary transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
                  >
                    <Icon name="user-plus" size={16} />
                  </button>
                </TH>
              )}
            </TR>
          </THead>
          <TBody>
            {accountsQ.data.map((account) => (
              <TR key={account.id}>
                <TD>
                  <div className="font-medium text-text-primary">{account.full_name}</div>
                  {account.job_title && (
                    <div className="text-text-tertiary text-xs">{account.job_title}</div>
                  )}
                </TD>
                <TD className="text-text-secondary">{account.login}</TD>
                <TD>
                  <Chip tone={account.role === 'admin' ? 'accent' : 'neutral'} variant="soft">
                    {account.role === 'admin' ? 'Администратор' : 'Участник'}
                  </Chip>
                </TD>
                <TD>
                  <StatusBadge status={account.status} />
                </TD>
                <TD className="text-text-secondary">
                  {account.project_access_count > 0
                    ? `${account.project_access_count} ${pluralRu(account.project_access_count, 'проект', 'проекта', 'проектов')}`
                    : '—'}
                </TD>
                {canManage && (
                  <TD>
                    <Menu
                      aria-label={`Действия — ${account.full_name}`}
                      trigger={
                        <span className="inline-flex p-1.5">
                          <Icon name="dots" size={16} />
                        </span>
                      }
                      items={[
                        {
                          label: 'Изменить',
                          icon: 'pencil',
                          onSelect: () => setDialog({ kind: 'edit', account }),
                        },
                        {
                          label: 'Доступ к проектам',
                          icon: 'columns',
                          onSelect: () => setDialog({ kind: 'access', account }),
                        },
                        account.status === 'active'
                          ? {
                              label: 'Отозвать доступ',
                              icon: 'close',
                              danger: true,
                              onSelect: () => toggleStatus(account),
                            }
                          : {
                              label: 'Восстановить доступ',
                              icon: 'check',
                              onSelect: () => toggleStatus(account),
                            },
                      ]}
                    />
                  </TD>
                )}
              </TR>
            ))}
          </TBody>
        </Table>
      )}

      {dialog?.kind === 'create' && (
        <AccountDialog companyId={companyId} open onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'edit' && (
        <AccountDialog
          companyId={companyId}
          account={dialog.account}
          open
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'access' && (
        <GrantProjectAccessDialog
          account={dialog.account}
          companyId={companyId}
          open
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  )
}
